import React from "react";
import { Link } from "react-router-dom";
import { Phone, Check, Shield, Sparkles, CalendarHeart, Building2, ArrowRight } from "lucide-react";
import { SITE } from "../data/site";
import QuoteForm from "../components/QuoteForm";

const WHY = [
  { icon: Sparkles, title: "Professional Staff", desc: "Uniformed valets trained for hospitality, safety and luxury car handling." },
  { icon: Shield, title: "Safety & Vehicle Care", desc: "Background-checked valets, fully insured operations, and detailed key tracking." },
  { icon: CalendarHeart, title: "Event Staffing", desc: "Scalable teams — from intimate dinners to 500-guest galas." },
  { icon: Building2, title: "Private & Corporate", desc: "Estate parties, restaurants, hotels, and corporate gatherings." },
];

const EVENTS = ["Weddings", "Restaurants", "Hotels", "Private Parties", "Corporate Events", "VIP Gatherings", "Special Occasions"];

export default function ZuberValet() {
  return (
    <div className="pt-20 bg-[#050505] min-h-screen" data-testid="zuber-page">
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <img src={SITE.heroMixed} alt="Zuber Valet Service" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/70 to-black/40" />
        <div className="relative max-w-7xl mx-auto px-6 pb-16 w-full grid lg:grid-cols-12 gap-10 items-end pt-32">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-4 mb-6">
              <img src={SITE.zuberLogo} alt="Zuber Valet" className="w-20 h-20 bg-white p-3 object-contain" />
              <div className="label-eyebrow">Zuber Valet Service · By USA Limo</div>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl text-white tracking-tight leading-[1.05]">
              Luxury Valet Service<br />for Premium Events
            </h1>
            <p className="text-white/75 text-lg mt-5 max-w-2xl">
              Professional Parking. First-Class Arrival. Reliable Valet Service Throughout the DMV.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#zuber-quote" className="btn-accent" data-testid="zuber-cta-quote">Request Valet Service <ArrowRight size={14} /></a>
              <a href={`tel:${SITE.phonesTel.office}`} className="btn-outline">
                <Phone size={14} /> Call {SITE.phones.office}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="label-eyebrow mb-3">Why Choose Zuber Valet</div>
            <h2 className="font-display text-4xl text-white tracking-tight">
              Discreet, polished, and event-ready — every single time.
            </h2>
            <p className="text-white/65 mt-5 leading-relaxed">
              From estate weddings in Loudoun to hotel galas in downtown DC, Zuber Valet delivers a
              hospitality-first arrival experience. Our valets are uniformed, trained, insured, and treat
              every car as a luxury asset.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {WHY.map((w) => (
              <div key={w.title} className="lux-card p-6" data-testid={`zuber-why-${w.title.replace(/\s+/g, "-").toLowerCase()}`}>
                <w.icon size={20} className="text-[#d4af6a] mb-3" />
                <h4 className="font-display text-xl text-white mb-2">{w.title}</h4>
                <p className="text-sm text-white/60 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-10">
            <div className="label-eyebrow mb-3">Where We Valet</div>
            <h2 className="font-display text-4xl text-white tracking-tight">
              Every event deserves a first-class arrival.
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {EVENTS.map((e) => (
              <div key={e} className="lux-card px-5 py-3 text-white/85" data-testid={`zuber-event-${e.replace(/\s+/g, "-").toLowerCase()}`}>{e}</div>
            ))}
          </div>
        </div>
      </section>

      <section id="zuber-quote" className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="max-w-2xl mb-10">
            <div className="label-eyebrow mb-3">Request A Quote</div>
            <h2 className="font-display text-4xl text-white tracking-tight">Plan your valet experience.</h2>
            <p className="text-white/65 mt-4">Tell us about your event — we'll respond within one business hour.</p>
          </div>
          <QuoteForm
            serviceType="valet"
            title="Request Valet Service"
            eyebrow="Zuber Valet"
            showGuestCount={false}
          />
        </div>
      </section>
    </div>
  );
}
